import {
  KnowledgeBaseEndpointsService,
  DataSourceEndpointsService,
} from '../services/knowledge-base';
import { getCurrentOrg } from './getCurrentOrg';
import { OpenAPI as KnowledgeManagement } from '../services/knowledge-base';
import type {
  Body_upload_text_to_data_source_knowledge_base_api_v1_data_source_upload_text_post,
  Body_upload_file_to_data_source_knowledge_base_api_v1_data_source_upload_file_post,
  DataSourceItem,
  DataSourceResponse,
  KnowledgeBaseShortInfoResponse,
  KnowledgeBaseTablesResponse,
  QueryRequest,
  QueryResponse,
  QueryResult,
  TableInfoRequest,
  UpdateTableRequest,
  SortBy,
  SortOrder,
} from '../services/knowledge-base';

export type KnowledgeBaseResponse = KnowledgeBaseTablesResponse;
export type KnowledgeSourceResponse = DataSourceResponse;
export type BaseKnowledgePayload = TableInfoRequest;
export type KnowledgeSource = DataSourceItem;
export type KnowledgeSourceItem = KnowledgeSource & { file_size?: string };
export type SearchKnowledgePayload = QueryRequest;
export type SearchKnowledgeResponse = QueryResponse;
export type SearchResult = QueryResult;
export type UploadKnowledgeSourceFile =
  Body_upload_file_to_data_source_knowledge_base_api_v1_data_source_upload_file_post;
export type UploadKnowledgeSourceText =
  Body_upload_text_to_data_source_knowledge_base_api_v1_data_source_upload_text_post;
export type RenameKnowledgeBase = UpdateTableRequest;
export type FireCrawlKnowledgePayload = {
  id_knowledge_base: string;
  task_type: string;
  user_id: string;
  file: Blob;
  active?: boolean;
  vectorize?: boolean;
  status?: string | null;
  name?: string | null;
};

export type ActivateKnowledgeSource = {
  dataSourceId: string;
  chunkSize?: number;
  chunkOverlap?: number;
};

export type KnowledgeSourceChunksPayload = {
  knowledgeBaseId: string;
  dataSourceId: string;
  page: number;
  limit: number;
};

export type BaseListKnowledgePayload = {
  includeInactive: boolean;
  page: number;
  limit: number;
  name?: string;
  organisationId: string;
  debug: boolean;
};

export type KnowledgeSourcePayload = {
  page: number;
  limit: number;
  idKnowledgeBase?: string | null;
  taskType?: string | null;
  fileType?: string | null;
  active?: boolean | null;
  status?: string | null;
  name?: string | null;
  sortBy: SortBy;
  sortOrder: SortOrder;
};

export const activateKnowledgeSource = ({
  dataSourceId,
  chunkSize,
  chunkOverlap,
}: ActivateKnowledgeSource): Promise<any> => {
  return DataSourceEndpointsService.vectorizeDataSourceKnowledgeBaseApiV1DataSourceDataSourceIdVectorizePost(
    dataSourceId,
    'text-embedding-3-small',
    chunkSize || 2500,
    chunkOverlap
  );
};

export const toggleActiveKnowledgeSource = (
  dataSourceId: string,
  active: boolean
): Promise<any> => {
  return DataSourceEndpointsService.toggleDataSourceActiveKnowledgeBaseApiV1DataSourceDataSourceIdToggleActivePatch(
    dataSourceId,
    active
  );
};

export const deleteKnowledgeSource = (dataSourceId: string): Promise<any> => {
  return DataSourceEndpointsService.deleteDataSourceKnowledgeBaseApiV1DataSourceDataSourceIdDelete(
    dataSourceId
  );
};

export const deleteKnowledgeBase = (knowledgeBaseId: string): Promise<any> => {
  return KnowledgeBaseEndpointsService.deleteKnowledgeBaseKnowledgeBaseApiV1KnowledgeBaseDeleteDelete(
    knowledgeBaseId
  );
};

export const renameKnowledgeBase = (
  tableId: string,
  {
    new_name: name,
    metadata_fields,
    active,
  }: RenameKnowledgeBase & { new_name: string }
): Promise<any> => {
  return KnowledgeBaseEndpointsService.updateKnowledgeBaseKnowledgeBaseApiV1KnowledgeBaseKnowledgeBaseIdUpdatePatch(
    tableId,
    {
      name: name,
      metadata_fields,
      active,
    }
  );
};

export const renameKnowledgeSource = (
  dataSourceId: string,
  newName: string
): Promise<any> => {
  return DataSourceEndpointsService.renameDataSourceKnowledgeBaseApiV1DataSourceDataSourceIdRenamePatch(
    dataSourceId,
    newName
  );
};

export const uploadKnowledgeSourceFile = ({
  id_knowledge_base,
  user_id,
  file,
  active,
  vectorize,
  status,
  name,
}: UploadKnowledgeSourceFile): Promise<any> => {
  return DataSourceEndpointsService.uploadFileToDataSourceKnowledgeBaseApiV1DataSourceUploadFilePost(
    {
      id_knowledge_base,
      user_id,
      file,
      active,
      vectorize,
      status,
      name,
    }
  );
};

export const uploadKnowledgeSourceText = ({
  id_knowledge_base,
  task_type,
  user_id,
  task_id,
  text,
  active,
  vectorize,
  chunk_size,
  chunk_overlap,
  status,
}: UploadKnowledgeSourceText): Promise<any> => {
  return DataSourceEndpointsService.uploadTextToDataSourceKnowledgeBaseApiV1DataSourceUploadTextPost(
    {
      id_knowledge_base,
      task_type,
      user_id,
      task_id,
      active,
      text,
      vectorize,
      chunk_size,
      chunk_overlap,
      status,
    }
  );
};

export const getAllKnowledgeSources = ({
  page,
  limit,
  idKnowledgeBase,
  taskType,
  fileType,
  active,
  status,
  name,
  sortBy,
  sortOrder,
}: KnowledgeSourcePayload): Promise<KnowledgeSourceResponse> => {
  return DataSourceEndpointsService.listDataSourcesKnowledgeBaseApiV1DataSourceListGet(
    page,
    limit,
    idKnowledgeBase,
    taskType,
    fileType,
    active,
    status,
    name,
    sortBy as SortBy,
    sortOrder as SortOrder
  );
};

export const createBaseKnowledge = ({
  name,
  user_id,
  active,
  metadata_fields,
}: BaseKnowledgePayload): Promise<any> => {
  return KnowledgeBaseEndpointsService.addTableToKnowledgeBaseKnowledgeBaseApiV1KnowledgeBaseAddTablePost(
    { name, organisation_id: getCurrentOrg(), user_id, active, metadata_fields }
  );
};

export const getBaseKnowledgeById = (knowledgeBaseId: string): Promise<any> => {
  return KnowledgeBaseEndpointsService.getKnowledgeBaseDetailKnowledgeBaseApiV1KnowledgeBaseDetailKnowledgeBaseIdGet(
    knowledgeBaseId
  );
};

export const searchKnowledge = (
  payload: SearchKnowledgePayload
): Promise<any> => {
  return KnowledgeBaseEndpointsService.queryDocumentsKnowledgeBaseApiV1KnowledgeBaseQueryPost(
    payload
  );
};

export const getKnowledgeSourceChunks = (
  payload: KnowledgeSourceChunksPayload
): Promise<any> => {
  return DataSourceEndpointsService.getDataSourceChunksKnowledgeBaseApiV1DataSourceChunksGet(
    payload.dataSourceId,
    payload.knowledgeBaseId,
    payload.page,
    payload.limit
  );
};

export const updateChunk = (
  knowledgeBaseId: string,
  chunkId: string,
  payload: { document?: string; metadata?: Record<string, any> }
): Promise<any> => {
  return DataSourceEndpointsService.updateChunkViewKnowledgeBaseApiV1KnowledgeBaseKnowledgeBaseIdChunkChunkIdPatch(
    knowledgeBaseId,
    chunkId,
    payload
  );
};

export type DeleteChunksPayload = {
  knowledgeBaseId: string;
  ids: string[];
};

export const deleteChunks = ({
  knowledgeBaseId,
  ids,
}: DeleteChunksPayload): Promise<any> => {
  return KnowledgeBaseEndpointsService.deleteChunksViewKnowledgeBaseApiV1KnowledgeBaseKnowledgeBaseIdChunkDelete(
    knowledgeBaseId,
    { ids }
  );
};

export const getAllKnowledges = (
  payload: Omit<BaseListKnowledgePayload, 'organisationId'>
): Promise<KnowledgeBaseResponse> => {
  const response =
    KnowledgeBaseEndpointsService.listAllTablesKnowledgeBaseApiV1KnowledgeBaseGet(
      payload.page,
      payload.limit,
      getCurrentOrg(),
      payload.name,
      payload.includeInactive
    );
  return response;
};

export type KnowledgeBaseShortInfo = KnowledgeBaseShortInfoResponse;

export const getKnowledgeBaseShortList = (
  name?: string | null,
  active: boolean = true
): Promise<KnowledgeBaseShortInfo[]> => {
  return KnowledgeBaseEndpointsService.knowledgeBaseShortListKnowledgeBaseApiV1KnowledgeBaseShortListGet(
    name,
    active,
    getCurrentOrg()
  );
};

const downloadKnowledgeItem = async (url: string): Promise<Blob> => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'text/csv',
      ...(KnowledgeManagement.TOKEN && {
        Authorization: `Bearer ${KnowledgeManagement.TOKEN}`,
      }),
    },
    redirect: 'follow',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Download failed: ${response.status} - ${errorText}`);
  }

  return response.blob();
};

export type ExtendKnowledgeByUrlPayload = {
  knowledgeBaseId: string;
  url: string;
  chunk_size: number;
  chunk_overlap: number;
};

export const extendKnowledgeByUrl = ({
  knowledgeBaseId,
  url,
  chunk_size,
  chunk_overlap,
}: ExtendKnowledgeByUrlPayload): Promise<any> => {
  return KnowledgeBaseEndpointsService.extendKnowledgeByUrlKnowledgeBaseApiV1KnowledgeBaseExtendKnowledgeByUrlKnowledgeBaseIdPost(
    knowledgeBaseId,
    { url, chunk_size, chunk_overlap }
  );
};

export const downloadKnowledgeBase = async (
  knowledgeBaseId: string
): Promise<Blob> => {
  const url = `${process.env.REACT_APP_BILLING_BASE_API_URL}/knowledge_base/api/v1/knowledge_base/${knowledgeBaseId}/download`;
  return downloadKnowledgeItem(url);
};

export const downloadKnowledgeSource = async (
  idKnowledgeBase: string,
  dataSourceId?: string
): Promise<Blob> => {
  const baseUrl = `${process.env.REACT_APP_BILLING_BASE_API_URL}/knowledge_base/api/v1/knowledge_base/${idKnowledgeBase}`;
  const url = dataSourceId
    ? `${baseUrl}/download?data_source_id=${dataSourceId}`
    : baseUrl;
  return downloadKnowledgeItem(url);
};
